// FREO: This component renders above the ChakraProvider (as a top-level
// error-boundary fallback), so Chakra semantic tokens cannot resolve here.
// Literal colors are required and the file is allowlisted in scripts/colors-allowlist.json.
import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useState } from "react";
import { FallbackProps } from "react-error-boundary";

const FONT_STACK =
  "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const BRAND = "#007df2";
const FG_DEFAULT = "#101010";
const FG_MUTED = "#616272";
const BG_PAGE = "#F2F2F2";
const BG_PANEL = "#FFFFFF";
const BORDER = "#DADCE0";
const STACK_BG = "#0F1013";
const STACK_FG = "#DADCE5";

const SHELL_STYLE: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: BG_PAGE,
  fontFamily: FONT_STACK,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 24px",
  color: FG_DEFAULT,
};

const CARD_STYLE: React.CSSProperties = {
  backgroundColor: BG_PANEL,
  border: `1px solid ${BORDER}`,
  borderRadius: "16px",
  boxShadow: "0 4px 16px rgba(15, 17, 23, 0.06)",
  padding: "40px",
  maxWidth: "560px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
};

const HEADING_STYLE: React.CSSProperties = {
  fontSize: "24px",
  lineHeight: "32px",
  letterSpacing: "-0.01em",
  fontWeight: 600,
  margin: 0,
};

const BODY_STYLE: React.CSSProperties = {
  fontSize: "14px",
  lineHeight: "20px",
  color: FG_MUTED,
  margin: 0,
};

const PRIMARY_BUTTON_STYLE: React.CSSProperties = {
  fontFamily: FONT_STACK,
  fontSize: "14px",
  fontWeight: 500,
  color: "#FFFFFF",
  backgroundColor: BRAND,
  border: "none",
  borderRadius: "8px",
  padding: "10px 16px",
  cursor: "pointer",
  height: "40px",
};

const SECONDARY_BUTTON_STYLE: React.CSSProperties = {
  fontFamily: FONT_STACK,
  fontSize: "14px",
  fontWeight: 500,
  color: FG_DEFAULT,
  backgroundColor: "transparent",
  border: `1px solid ${BORDER}`,
  borderRadius: "8px",
  padding: "10px 16px",
  cursor: "pointer",
  height: "40px",
};

const LINK_BUTTON_STYLE: React.CSSProperties = {
  fontFamily: FONT_STACK,
  fontSize: "13px",
  color: FG_MUTED,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
  alignSelf: "flex-start",
  textDecoration: "underline",
};

const STACK_PRE_STYLE: React.CSSProperties = {
  margin: 0,
  padding: "16px",
  backgroundColor: STACK_BG,
  color: STACK_FG,
  borderRadius: "8px",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "12px",
  lineHeight: "18px",
  maxHeight: "200px",
  overflow: "auto",
  whiteSpace: "pre-wrap",
  textAlign: "left",
};

const ACTIONS_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const { logout } = useAuth0();
  const [showError, setShowError] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<boolean>(false);

  // FallbackProps.error is typed as `{}` since errors thrown anywhere can be
  // anything. Narrow once at the top so the JSX below can access .stack/.message
  // without TS2339.
  const normalizedError =
    error instanceof Error
      ? error
      : new Error(typeof error === "string" ? error : "Unknown error");

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsCopied(false);
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [isCopied]);

  return (
    <div style={SHELL_STYLE}>
      <div style={CARD_STYLE}>
        <h1 style={HEADING_STYLE}>Something went wrong</h1>
        <p style={BODY_STYLE}>
          The app hit an unexpected error. Refreshing usually fixes it. If the
          problem persists, try logging out and back in, or send us the error
          details so we can look into it.
        </p>

        <div style={ACTIONS_ROW_STYLE}>
          <button
            type="button"
            style={PRIMARY_BUTTON_STYLE}
            onClick={() => {
              if (resetErrorBoundary) {
                resetErrorBoundary();
              } else {
                window.location.reload();
              }
            }}
          >
            Refresh the page
          </button>
          <button
            type="button"
            style={SECONDARY_BUTTON_STYLE}
            onClick={() => logout()}
          >
            Log out
          </button>
        </div>

        {error ? (
          <button
            type="button"
            style={LINK_BUTTON_STYLE}
            onClick={() => setShowError(!showError)}
          >
            {showError ? "Hide" : "Show"} technical details
          </button>
        ) : null}

        {showError && error ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <button
              type="button"
              style={SECONDARY_BUTTON_STYLE}
              onClick={() => {
                setIsCopied(true);
                if (normalizedError.stack) {
                  navigator.clipboard.writeText(normalizedError.stack);
                }
              }}
            >
              {isCopied ? "Copied" : "Copy error to clipboard"}
            </button>
            <pre style={STACK_PRE_STYLE}>
              {normalizedError.stack || normalizedError.message}
            </pre>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ErrorFallback;
